import moment, { type Moment } from 'moment';
import type { IntlShape } from 'react-intl';

import { DateUtil } from '@src/packages/utils/dates';

import { isRelativePeriodValue } from './resolvePeriodToMillis';

export interface FormatAbsolutePeriodOptions {
  /** Moment format applied to both bounds. */
  format?: string;
  separator?: string;
}

/**
 * Active-filter tag text for a relative period: "3 Days".
 *
 * The `dateTypes.*` messages only carry the pluralized unit ("Day" / "Days"), so the amount is
 * prefixed here. Anything that is not a relative period formats as '' — the tag stays hidden and
 * the absolute range renders its own tag instead.
 */
export const formatRelativePeriodForTag = (value: unknown, intl: IntlShape): string => {
  if (!isRelativePeriodValue(value)) return '';

  const { relativeAmount, dateType } = value;

  return `${relativeAmount} ${intl.formatMessage(
    { id: `dateTypes.${dateType}` },
    { amount: relativeAmount },
  )}`;
};

/** Active-filter tag text for an absolute period: "03/02/2026 – 03/08/2026". */
export const formatAbsolutePeriodForTag = (
  dateRange: [Moment, Moment] | null | undefined,
  {
    format = DateUtil.DEFAULT_DATE_FORMAT,
    separator = ' – ',
  }: FormatAbsolutePeriodOptions = {},
): string => {
  if (!dateRange?.[0] || !dateRange[1]) return '';

  return `${moment(dateRange[0]).format(format)}${separator}${moment(dateRange[1]).format(format)}`;
};
